import { useState, useRef, useCallback, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { previewCustomerPDF, previewInvoicePDF } from "@/lib/pdf";
import { uploadFile as uploadFileToStorage } from "@/lib/upload";
import {
  useGetSettings,
  useUpdateSettings,
  getGetSettingsQueryKey,
  useGetConnectStatus,
  useStartConnectOnboarding,
  useGetConnectDashboardLink,
  CONNECT_STATUS_QUERY_KEY,
  useSaveServiceAgreement,
  useDeleteServiceAgreement,
} from "@workspace/api-client-react";
import { detectLocaleFromCountry, getTimezonesForCountry, COMMON_TIMEZONES } from "@workspace/api-zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Building2, Phone, Mail, Globe, MapPin, Upload, Trash2, Save, Loader2,
  AlertCircle, Plus, X, Palette, FileText, Columns, ReceiptText, LogOut, Bell,
  ShieldAlert, User, KeyRound, CheckCircle2, CreditCard, Sparkles, ExternalLink,
  Zap, CheckCircle, Eye, RefreshCw, Lock, FileSignature, Download, Settings as SettingsIcon, Target,
} from "lucide-react";
import { CollapsibleSection } from "@/components/collapsible-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth, getTrialInfo, hasBrandingAccess } from "@/hooks/use-auth";
import { ProGate } from "@/components/pro-gate";
import { useLocation, Link } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ColumnConfig = {
  description: { visible: boolean; label: string };
  qty: { visible: boolean; label: string };
  unitPrice: { visible: boolean; label: string };
  amount: { visible: boolean; label: string };
};

type TaxLine = {
  name: string;
  rate: number | undefined;
  taxId?: string;
  externalSource?: string;
  externalId?: string;
};

const DEFAULT_COLUMN_CONFIG: ColumnConfig = {
  description: { visible: true, label: "Services" },
  qty: { visible: true, label: "Qty" },
  unitPrice: { visible: true, label: "Unit Price" },
  amount: { visible: true, label: "Amount" },
};

function parseColumnConfig(raw: string | null | undefined): ColumnConfig {
  if (!raw) return DEFAULT_COLUMN_CONFIG;
  try {
    const p = JSON.parse(raw);
    return {
      description: p.description ?? DEFAULT_COLUMN_CONFIG.description,
      qty: p.qty ?? DEFAULT_COLUMN_CONFIG.qty,
      unitPrice: p.unitPrice ?? DEFAULT_COLUMN_CONFIG.unitPrice,
      amount: p.amount ?? DEFAULT_COLUMN_CONFIG.amount,
    };
  } catch { return DEFAULT_COLUMN_CONFIG; }
}

function parseTaxLines(raw: string | null | undefined): TaxLine[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch { return []; }
}

const settingsSchema = z.object({
  companyName: z.string().optional(),
  address: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email({ message: "Invalid email" }).optional().or(z.literal("")),
  website: z.string().optional(),
  tagline: z.string().optional(),
  logoBase64: z.string().optional(),
  accentColor: z.string().optional(),
  documentTitle: z.string().optional(),
  currencyLabel: z.string().optional(),
  termsAndNotes: z.string().optional(),
  invoiceDocumentTitle: z.string().optional(),
  invoiceAccentColor: z.string().optional(),
  invoiceCurrencyLabel: z.string().optional(),
  invoiceTermsAndNotes: z.string().optional(),
  notifyOverdueInvoices: z.boolean().optional(),
  notifyTodoReminders: z.boolean().optional(),
  todoReminderLeadDays: z.coerce.number().int().min(1).max(30).optional(),
  notifyRecurringGenerated: z.boolean().optional(),
  notifyPaymentReceived: z.boolean().optional(),
  notificationHour: z.coerce.number().int().min(0).max(23).optional(),
  targetMargin: z.coerce.number({ invalid_type_error: "Enter a whole number" }).int("Use a whole number").min(0, "0% or more").max(90, "90% or less").optional(),
  pdfFooter: z.string().optional(),
  invoicePdfFooter: z.string().optional(),
  timezone: z.string().optional(),
  replyToEmail: z.string().email({ message: "Invalid email" }).optional().or(z.literal("")),
  defaultQuoteEmailSubject: z.string().optional(),
  defaultQuoteEmailBody: z.string().optional(),
  defaultInvoiceEmailSubject: z.string().optional(),
  defaultInvoiceEmailBody: z.string().optional(),
});

type SettingsForm = z.infer<typeof settingsSchema>;

const ALL_SECTION_IDS = [
  "section-branding", "section-business", "section-taxes", "section-quote-template",
  "section-invoice-template", "section-email-defaults", "section-service-agreement",
  "section-notifications", "section-billing", "section-accept-payments",
  "section-account", "section-danger",
];

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export default function Settings() {
  usePageTitle("Settings");
  const { toast } = useToast();
  const { user, logout, refreshUser } = useAuth();
  const { hasProAccess: isPro, isOnTrial } = getTrialInfo(user);
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const serviceAgreementInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [serviceAgreementUploading, setServiceAgreementUploading] = useState(false);
  const [serviceAgreementError, setServiceAgreementError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>(DEFAULT_COLUMN_CONFIG);
  const [taxLines, setTaxLines] = useState<TaxLine[]>([]);
  const [invoiceColumnConfig, setInvoiceColumnConfig] = useState<ColumnConfig>(DEFAULT_COLUMN_CONFIG);
  const [reEngagementEnabled, setReEngagementEnabled] = useState(true);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showQuotePdfPreview, setShowQuotePdfPreview] = useState(false);
  const [quotePdfBlobUrl, setQuotePdfBlobUrl] = useState<string | null>(null);
  const [showInvoicePdfPreview, setShowInvoicePdfPreview] = useState(false);
  const [invoicePdfBlobUrl, setInvoicePdfBlobUrl] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    Object.fromEntries(ALL_SECTION_IDS.map(id => [id, false]))
  );

  const toggleSection = (id: string) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const [profileName, setProfileName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileStatus, setProfileStatus] = useState<{ ok?: boolean; error?: string } | null>(null);

  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{ ok?: boolean; error?: string } | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<{ ok?: boolean; error?: string } | null>(null);

  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    status: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
  } | null>(null);


  const { data: settings, isLoading } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() }
  });

  const { data: connectStatus, refetch: refetchConnectStatus } = useGetConnectStatus({
    query: { queryKey: [...CONNECT_STATUS_QUERY_KEY] }
  });

  const connectOnboardingMutation = useStartConnectOnboarding();
  const connectDashboardMutation = useGetConnectDashboardLink();

  const connectOnboardingLoading = connectOnboardingMutation.isPending;
  const connectDashboardLoading = connectDashboardMutation.isPending;

  const updateSettings = useUpdateSettings();
  const saveServiceAgreementMutation = useSaveServiceAgreement();
  const deleteServiceAgreementMutation = useDeleteServiceAgreement();

  // Tax labels can be managed by an external accounting integration (currently
  // QuickBooks). When the integration is connected and not in "skip" mode, we
  // lock the entire tax section: no add/delete/inline edits, just a refresh
  // button (use_qbo) or a manage-in-QBO link.
  // QuickBooks integration is hidden behind "Coming Soon" — task #762.
  // Treat tax labels as if QB is disconnected: no "Managed by QuickBooks"
  // banner, no "Refresh from QuickBooks" button, no per-row QB lock. Stale
  // rows in the database with externalSource === "quickbooks" are handled
  // below by excluding them from the "managed" check. The QB hooks
  // (useGetQBOStatus, useRefreshQBOTaxLabels) are intentionally not called
  // — the underlying API client functions stay in @workspace/api-client-react
  // for when the integration is unhidden.
  const qboConnected = false;
  const qboTaxDirection: "use_qbo" | "push_to_qbo" | "skip" | null = null;
  const qboManagesTax = false;
  const qboCompanyName = "";
  const qboRealmId = "";
  const refreshQboTaxLabels = {
    isPending: false,
    mutate: (
      _input: undefined,
      _opts?: {
        onSuccess?: (res: { added: number }) => void;
        onError?: (err: { message?: string }) => void;
      },
    ) => {},
  };

  useEffect(() => {
    if (settings) {
      setColumnConfig(parseColumnConfig(settings.columnConfig));
      setTaxLines(parseTaxLines(settings.taxLines));
      setInvoiceColumnConfig(parseColumnConfig(settings.invoiceColumnConfig));
      const prefs = settings.emailPreferences as Record<string, unknown> | null | undefined;
      setReEngagementEnabled(prefs?.reEngagement !== false);
    }
  }, [settings]);


  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    values: {
      companyName: settings?.companyName ?? "",
      address: settings?.address ?? "",
      addressLine1: settings?.addressLine1 ?? "",
      addressLine2: settings?.addressLine2 ?? "",
      city: settings?.city ?? "",
      state: settings?.state ?? "",
      postalCode: settings?.postalCode ?? "",
      country: settings?.country ?? "",
      phone: settings?.phone ?? "",
      email: settings?.email ?? "",
      website: settings?.website ?? "",
      tagline: settings?.tagline ?? "",
      logoBase64: settings?.logoBase64 ?? "",
      accentColor: settings?.accentColor ?? "#1e3a5f",
      documentTitle: settings?.documentTitle ?? "QUOTATION",
      currencyLabel: settings?.currencyLabel ?? "USD",
      termsAndNotes: settings?.termsAndNotes ?? "",
      invoiceDocumentTitle: settings?.invoiceDocumentTitle ?? "INVOICE",
      invoiceAccentColor: settings?.invoiceAccentColor ?? "",
      invoiceCurrencyLabel: settings?.invoiceCurrencyLabel ?? "",
      invoiceTermsAndNotes: settings?.invoiceTermsAndNotes ?? "",
      notifyOverdueInvoices: settings?.notifyOverdueInvoices ?? true,
      notifyTodoReminders: settings?.notifyTodoReminders ?? true,
      todoReminderLeadDays: settings?.todoReminderLeadDays ?? 1,
      notifyRecurringGenerated: settings?.notifyRecurringGenerated ?? true,
      notifyPaymentReceived: settings?.notifyPaymentReceived ?? true,
      notificationHour: settings?.notificationHour ?? 6,
      targetMargin: settings?.targetMargin ?? 30,
      pdfFooter: settings?.pdfFooter ?? "Created with QuoTrak — Professional quoting & invoicing made simple.\nhttps://quotrak.com",
      invoicePdfFooter: settings?.invoicePdfFooter ?? "Created with QuoTrak — Professional quoting & invoicing made simple.\nhttps://quotrak.com",
      timezone: settings?.timezone ?? "",
      replyToEmail: settings?.replyToEmail ?? "",
      defaultQuoteEmailSubject: settings?.defaultQuoteEmailSubject ?? "",
      defaultQuoteEmailBody: settings?.defaultQuoteEmailBody ?? "",
      defaultInvoiceEmailSubject: settings?.defaultInvoiceEmailSubject ?? "",
      defaultInvoiceEmailBody: settings?.defaultInvoiceEmailBody ?? "",
    },
  });

  const handleCountryChange = useCallback((countryValue: string) => {
    const detected = detectLocaleFromCountry(countryValue);
    if (!detected) return;
    form.setValue("timezone", detected.timezone, { shouldDirty: true });
    form.setValue("currencyLabel", detected.currency, { shouldDirty: true });
    setIsDirty(true);
  }, [form]);

  const watchedCountry = form.watch("country");
  const watchedTimezone = form.watch("timezone");
  const timezoneGroups = (() => {
    const groups = getTimezonesForCountry(watchedCountry ?? "") ?? COMMON_TIMEZONES;
    const currentTz = watchedTimezone?.trim();
    if (!currentTz) return groups;
    const allZones = groups.flatMap((g) => g.zones);
    if (allZones.includes(currentTz)) return groups;
    return [{ label: "Current", zones: [currentTz] }, ...groups];
  })();

  const currentLogo = logoPreview ?? settings?.logoBase64 ?? null;

  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Image exceeds 2 MB. Please choose a smaller file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const original = reader.result as string;
      // Resize to ≤600 px wide before storing so jsPDF doesn't embed a
      // multi-megabyte raw bitmap, which can push email attachments over
      // Resend's 40 MB limit on high-resolution source images.
      const img = new Image();
      img.onload = () => {
        const MAX_W = 600;
        const scale = img.naturalWidth > MAX_W ? MAX_W / img.naturalWidth : 1;
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.naturalWidth  * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const resized = canvas.toDataURL("image/png", 0.92);
          setLogoPreview(resized);
          form.setValue("logoBase64", resized, { shouldDirty: true });
        } else {
          setLogoPreview(original);
          form.setValue("logoBase64", original, { shouldDirty: true });
        }
        setIsDirty(true);
        emitTourEvent("settings.logo-saved");
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  }, [form, fileInputRef]);

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoError(null);
    form.setValue("logoBase64", "", { shouldDirty: true });
    setIsDirty(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleServiceAgreementUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setServiceAgreementError(null);
    if (file.type !== "application/pdf") {
      setServiceAgreementError("Only PDF files are allowed.");
      if (serviceAgreementInputRef.current) serviceAgreementInputRef.current.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setServiceAgreementError("File exceeds 10 MB. Please choose a smaller file.");
      if (serviceAgreementInputRef.current) serviceAgreementInputRef.current.value = "";
      return;
    }
    setServiceAgreementUploading(true);
    try {
      const { objectPath } = await uploadFileToStorage(file);

      await saveServiceAgreementMutation.mutateAsync({
        data: { objectPath, fileName: file.name },
      });
      await queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
      toast({ title: "Service agreement uploaded", description: file.name });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setServiceAgreementError(msg);
      toast({ title: "Upload failed", description: msg, variant: "destructive" });
    } finally {
      setServiceAgreementUploading(false);
      if (serviceAgreementInputRef.current) serviceAgreementInputRef.current.value = "";
    }
  }, [saveServiceAgreementMutation, queryClient, toast]);

  const handleRemoveServiceAgreement = useCallback(async () => {
    setServiceAgreementError(null);
    try {
      await deleteServiceAgreementMutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
      toast({ title: "Service agreement removed" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to remove";
      setServiceAgreementError(msg);
      toast({ title: "Remove failed", description: msg, variant: "destructive" });
    }
  }, [deleteServiceAgreementMutation, queryClient, toast]);

  const handleDownloadServiceAgreement = useCallback(async () => {
    try {
      const res = await fetch("/api/settings/service-agreement/download", { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Download failed";
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    }
  }, [toast]);

  const updateColConfig = (
    key: keyof ColumnConfig,
    field: "visible" | "label",
    value: boolean | string
  ) => {
    setColumnConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
    setIsDirty(true);
  };

  const addTaxLine = () => {
    setTaxLines(prev => [...prev, { name: "", rate: undefined, taxId: "" }]);
    setIsDirty(true);
  };

  const updateTaxLine = (i: number, field: keyof TaxLine, value: string | number | undefined) => {
    setTaxLines(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
    setIsDirty(true);
  };

  const removeTaxLine = (i: number) => {
    setTaxLines(prev => prev.filter((_, idx) => idx !== i));
    setIsDirty(true);
  };

  const updateInvoiceColConfig = (
    key: keyof ColumnConfig,
    field: "visible" | "label",
    value: boolean | string
  ) => {
    setInvoiceColumnConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
    setIsDirty(true);
  };

  const handleQuotePdfPreview = useCallback(() => {
    const vals = form.getValues();
    const sampleEstimate = {
      id: 42,
      estimateNumber: "0042",
      title: "Kitchen Renovation",
      status: "sent",
      customerName: "Alex Johnson",
      customerPhone: "(555) 867-5309",
      customerEmail: "alex.johnson@email.com",
      total: 4250,
      subtotal: 4250,
      taxEnabled: false,
      taxRate: 0,
      depositType: "percent" as const,
      depositValue: 25,
      estimateDate: new Date().toISOString().slice(0, 10),
      validUntil: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      createdAt: new Date().toISOString(),
      lineItems: [
        { description: "Demo existing cabinets and prep surfaces", quantity: 1, unitPrice: 850, total: 850 },
        { description: "Supply & install new shaker cabinets", quantity: 8, unitPrice: 280, total: 2240 },
        { description: "Countertop installation (quartz)", quantity: 1, unitPrice: 1160, total: 1160 },
      ],
    };
    const company = {
      companyName: vals.companyName || undefined,
      addressLine1: vals.addressLine1 || undefined,
      addressLine2: vals.addressLine2 || undefined,
      city: vals.city || undefined,
      state: vals.state || undefined,
      postalCode: vals.postalCode || undefined,
      country: vals.country || undefined,
      address: vals.address || undefined,
      phone: vals.phone || undefined,
      email: vals.email || undefined,
      website: vals.website || undefined,
      logoBase64: logoPreview ?? settings?.logoBase64 ?? undefined,
      accentColor: vals.accentColor || "#1e3a5f",
      documentTitle: vals.documentTitle || "QUOTATION",
      currencyLabel: vals.currencyLabel || "USD",
      columnConfig: JSON.stringify(columnConfig),
      termsAndNotes: vals.termsAndNotes || undefined,
      pdfFooter: vals.pdfFooter || undefined,
      timezone: vals.timezone || undefined,
    };
    setQuotePdfBlobUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    const url = previewCustomerPDF(sampleEstimate, company);
    setQuotePdfBlobUrl(url);
    setShowQuotePdfPreview(true);
  }, [form, logoPreview, settings, columnConfig]);

  const handleInvoicePdfPreview = useCallback(async () => {
    const vals = form.getValues();
    const sampleInvoice = {
      id: 17,
      invoiceNumber: "INV-0017",
      title: "Bathroom Remodel",
      status: "sent",
      customerName: "Jordan Smith",
      customerPhone: "(555) 234-5678",
      customerEmail: "jordan.smith@email.com",
      total: 3200,
      subtotal: 3200,
      taxEnabled: false,
      taxRate: 0,
      invoiceDate: new Date().toISOString().slice(0, 10),
      dueDate: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      createdAt: new Date().toISOString(),
      lineItems: [
        { description: "Tile removal and surface prep", quantity: 1, unitPrice: 600, total: 600 },
        { description: "Supply & install floor tile", quantity: 40, unitPrice: 32, total: 1280 },
        { description: "Vanity & fixtures installation", quantity: 1, unitPrice: 1320, total: 1320 },
      ],
    };
    const company = {
      companyName: vals.companyName || undefined,
      addressLine1: vals.addressLine1 || undefined,
      addressLine2: vals.addressLine2 || undefined,
      city: vals.city || undefined,
      state: vals.state || undefined,
      postalCode: vals.postalCode || undefined,
      country: vals.country || undefined,
      address: vals.address || undefined,
      phone: vals.phone || undefined,
      email: vals.email || undefined,
      website: vals.website || undefined,
      logoBase64: logoPreview ?? settings?.logoBase64 ?? undefined,
      accentColor: vals.accentColor || "#1e3a5f",
      invoiceAccentColor: undefined, // always inherit from branding colour
      invoiceDocumentTitle: vals.invoiceDocumentTitle || "INVOICE",
      invoiceCurrencyLabel: vals.invoiceCurrencyLabel || undefined,
      currencyLabel: vals.currencyLabel || "USD",
      invoiceColumnConfig: JSON.stringify(invoiceColumnConfig),
      invoiceTermsAndNotes: vals.invoiceTermsAndNotes || undefined,
      invoicePdfFooter: vals.invoicePdfFooter || undefined,
      timezone: vals.timezone || undefined,
    };
    setInvoicePdfBlobUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    const url = await previewInvoicePDF(sampleInvoice, company);
    setInvoicePdfBlobUrl(url);
    setShowInvoicePdfPreview(true);
  }, [form, logoPreview, settings, invoiceColumnConfig]);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    if (user?.name) {
      setProfileName(user.name);
    }
  }, [user?.name]);

  const handleUpdateProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileStatus(null);
    try {
      const res = await fetch(`${BASE}/api/auth/profile`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: profileName,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      await refreshUser();
      setProfileStatus({ ok: true });
    } catch (err) {
      setProfileStatus({ error: err instanceof Error ? err.message : "An error occurred" });
    } finally {
      setProfileLoading(false);
    }
  }, [BASE, profileName, refreshUser]);

  const handleChangeEmail = useCallback(async () => {
    setEmailLoading(true);
    setEmailStatus(null);
    try {
      const res = await fetch(`${BASE}/api/auth/change-email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      setEmailStatus({ ok: true });
      setNewEmail("");
    } catch (err) {
      setEmailStatus({ error: err instanceof Error ? err.message : "An error occurred" });
    } finally {
      setEmailLoading(false);
    }
  }, [BASE, newEmail]);

  const handleChangePassword = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ error: "New passwords do not match" });
      return;
    }
    setPasswordLoading(true);
    setPasswordStatus(null);
    try {
      const res = await fetch(`${BASE}/api/auth/change-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      setPasswordStatus({ ok: true });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordStatus({ error: err instanceof Error ? err.message : "An error occurred" });
    } finally {
      setPasswordLoading(false);
    }
  }, [BASE, currentPassword, newPassword, confirmPassword]);

  useEffect(() => {
    if (!isPro) return;
    fetch(`${BASE}/api/billing/subscription`, { credentials: "include" })
      .then(r => r.json())
      .then((data: { subscription: { status: string; currentPeriodEnd: number; cancelAtPeriodEnd: boolean } | null }) => {
        if (data.subscription) setSubscriptionInfo(data.subscription);
      })
      .catch(() => null);
  }, [BASE, user?.plan]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connect = params.get("connect");
    if (connect === "return" || connect === "refresh" || connect === "manage") {
      refetchConnectStatus();
      setOpenSections(prev => ({ ...prev, "section-accept-payments": true }));
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => scrollToSection("section-accept-payments"), 100);
    }
  }, [refetchConnectStatus]);

  // Open + scroll to a section when the URL fragment points at one. Used by the
  // guided onboarding tour (and by any deep link like /settings#section-branding).
  useEffect(() => {
    const openFromHash = () => {
      const id = window.location.hash.replace(/^#/, "");
      if (id && ALL_SECTION_IDS.includes(id)) {
        setOpenSections(prev => ({ ...prev, [id]: true }));
        setTimeout(() => scrollToSection(id), 120);
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  const handleBillingPortal = useCallback(async () => {
    setBillingPortalLoading(true);
    try {
      const res = await fetch(`${BASE}/api/billing/portal`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast({
        title: "Could not open billing portal",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setBillingPortalLoading(false);
    }
  }, [BASE, toast]);

  const handleConnectOnboarding = useCallback(async () => {
    try {
      const data = await connectOnboardingMutation.mutateAsync();
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      toast({
        title: "Could not start Stripe Connect onboarding",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  }, [connectOnboardingMutation, toast]);

  const handleConnectDashboard = useCallback(async () => {
    try {
      const data = await connectDashboardMutation.mutateAsync();
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      toast({
        title: "Could not open Stripe dashboard",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  }, [connectDashboardMutation, toast]);

  const handleDeleteAccount = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${BASE}/api/auth/account`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `Deletion failed (${res.status})`);
      }
      await logout();
      navigate("/login?deleted=1");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "An unexpected error occurred. Please try again.");
      setIsDeleting(false);
    }
  }, [BASE, logout, navigate]);

  const onSubmit = (data: SettingsForm) => {
    // The form holds blanks until the saved settings arrive; saving then would
    // send those blanks over the real values.
    if (!settings) return;
    const payload = {
      ...data,
      columnConfig: JSON.stringify(columnConfig),
      taxLines: JSON.stringify(taxLines.map(t => ({ ...t, rate: t.rate ?? 0 }))),
      invoiceColumnConfig: JSON.stringify(invoiceColumnConfig),
      notifyOverdueInvoices: data.notifyOverdueInvoices ?? true,
      notifyTodoReminders: data.notifyTodoReminders ?? true,
      todoReminderLeadDays: data.todoReminderLeadDays ?? 1,
      notifyRecurringGenerated: data.notifyRecurringGenerated ?? true,
      notifyPaymentReceived: data.notifyPaymentReceived ?? true,
      notificationHour: data.notificationHour ?? 6,
      targetMargin: data.targetMargin ?? 30,
      emailPreferences: { reEngagement: reEngagementEnabled },
    };
    updateSettings.mutate({ data: payload }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        setIsDirty(false);
        toast({ title: "Settings saved", description: "Your settings have been updated." });
        // Lets the setup checklist re-check the business details item.
        emitTourEvent("settings.saved");
      },
      onError: () => {
        toast({ title: "Failed to save", description: "Please try again.", variant: "destructive" });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px]" />
        <Skeleton className="h-[300px]" />
      </div>
    );
  }

  const visibleColCount = Object.values(columnConfig).filter(c => c.visible).length;
  const invoiceVisibleColCount = Object.values(invoiceColumnConfig).filter(c => c.visible).length;

  const colKeys: Array<{ key: keyof ColumnConfig; defaultLabel: string }> = [
    { key: "description", defaultLabel: "Services / Description" },
    { key: "qty", defaultLabel: "Quantity" },
    { key: "unitPrice", defaultLabel: "Unit Price" },
    { key: "amount", defaultLabel: "Amount / Total" },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-8 pb-16 animate-in fade-in duration-300">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <SettingsIcon className="w-8 h-8 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your company profile, quote template, and invoice template.
          </p>
        </div>
        <Button
          type="button"
          data-tour-target="settings-save-btn"
          onClick={() => form.handleSubmit(onSubmit)()}
          disabled={updateSettings.isPending || !isDirty || !settings}
          className="shrink-0 mt-1"
        >
          {updateSettings.isPending ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-1.5" /> Save Settings</>
          )}
        </Button>
      </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onChange={() => setIsDirty(true)} className="space-y-6">

            {/* ── BRANDING ─────────────────────────────── */}
            <CollapsibleSection
              id="section-branding"
              title="Branding"
              icon={<Palette className="w-5 h-5" />}
              isOpen={openSections["section-branding"]}
              onToggle={() => toggleSection("section-branding")}
              dataTourTarget="branding"
            >
              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" /> Company Logo
                  </CardTitle>
                  <CardDescription>
                    Appears in the top-left of exported PDFs. PNG or JPG, under 2 MB.
                    {isOnTrial && !user?.brandingGrandfathered && " Part of Pro, included in your trial."}
                    {!hasBrandingAccess(user) && " Available on Pro."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <ProGate feature="COMPANY_LOGO" mode="overlay" unlocked={hasBrandingAccess(user)}>
                  <div className="flex items-start gap-6">
                    <div className="shrink-0">
                      {currentLogo ? (
                        <div className="relative w-28 h-20 border border-border rounded-lg overflow-hidden bg-muted/30 flex items-center justify-center">
                          <img src={currentLogo} alt="Company logo preview" className="max-w-full max-h-full object-contain p-1" />
                        </div>
                      ) : (
                        <div className="w-28 h-20 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center bg-muted/20 text-muted-foreground">
                          <Building2 className="w-6 h-6 mb-1 opacity-40" />
                          <span className="text-[10px] text-center leading-tight">No logo</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 flex-1">
                      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                      <Button type="button" variant="outline" size="sm" onClick={() => { setLogoError(null); fileInputRef.current?.click(); }} className="w-full">
                        <Upload className="w-4 h-4 mr-2" /> Upload Logo
                      </Button>
                      {logoError && (
                        <p className="text-xs text-destructive flex items-start gap-1">
                          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                          {logoError}
                        </p>
                      )}
                      {currentLogo && (
                        <Button type="button" variant="ghost" size="sm" onClick={handleRemoveLogo} className="w-full text-destructive hover:text-destructive hover:bg-destructive/10">
                          <Trash2 className="w-4 h-4 mr-2" /> Remove Logo
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">For best results, use a logo with a transparent or white background.</p>
                    </div>
                  </div>
                  </ProGate>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Palette className="w-4 h-4 text-muted-foreground" /> Accent Color
                  </CardTitle>
                  <CardDescription>
                    Tints headings and totals on all your customer PDFs.
                    {isOnTrial && !user?.brandingGrandfathered && " Part of Pro, included in your trial."}
                    {!hasBrandingAccess(user) && " Available on Pro."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <ProGate feature="ACCENT_COLOR" mode="overlay" unlocked={hasBrandingAccess(user)}>
                  <FormField control={form.control} name="accentColor" render={({ field }) => (
                    <FormItem data-tour-target="accent-color">
                      <FormControl>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            className="h-9 w-14 rounded-md border border-input cursor-pointer bg-transparent p-0.5"
                            value={field.value || "#1e3a5f"}
                            onChange={e => { field.onChange(e.target.value); setIsDirty(true); }}
                          />
                          <Input
                            placeholder="#1e3a5f"
                            value={field.value || ""}
                            onChange={e => { field.onChange(e.target.value); setIsDirty(true); }}
                            className="font-mono"
                            maxLength={7}
                          />
                          <div
                            className="h-9 w-9 rounded-md border border-border shrink-0"
                            style={{ background: field.value || "#1e3a5f" }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  </ProGate>
                </CardContent>
              </Card>
            </CollapsibleSection>

            {/* ── BUSINESS INFO ─────────────────────────────── */}
            <CollapsibleSection
              id="section-business"
              title="Business Information"
              icon={<Building2 className="w-5 h-5" />}
              isOpen={openSections["section-business"]}
              onToggle={() => toggleSection("section-business")}
              dataTourTarget="business-details"
            >
              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardDescription>Shown on all exported documents.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-5">
                  <FormField control={form.control} name="companyName" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" /> Company Name</FormLabel>
                      <FormControl><Input placeholder="Your Business Name" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="tagline" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tagline / Slogan</FormLabel>
                      <FormControl><Input placeholder="Licensed & Insured • Est. 2005" {...field} /></FormControl>
                      <FormDescription>Optional. Appears below your company name on Cost Report PDFs.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="phone" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> Phone</FormLabel>
                        <FormControl><Input placeholder="(555) 123-4567" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="email" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> Email</FormLabel>
                        <FormControl><Input type="email" placeholder="you@yourcompany.com" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="website" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-muted-foreground" /> Website</FormLabel>
                      <FormControl><Input placeholder="www.yourcompany.com" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div className="space-y-3">
                    <p className="text-sm font-medium flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /> Business Address</p>
                    <FormField control={form.control} name="addressLine1" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs text-muted-foreground">Address Line 1</FormLabel>
                        <FormControl><Input placeholder="123 Main Street" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="addressLine2" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs text-muted-foreground">Address Line 2 <span className="font-normal">(optional)</span></FormLabel>
                        <FormControl><Input placeholder="Suite 100" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="city" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">City</FormLabel>
                          <FormControl><Input placeholder="Portland" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="state" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">State / Province</FormLabel>
                          <FormControl><Input placeholder="OR" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="postalCode" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Postal Code</FormLabel>
                          <FormControl><Input placeholder="97201" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="country" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Country</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="United States"
                              {...field}
                              onBlur={() => {
                                field.onBlur();
                                handleCountryChange(field.value ?? "");
                              }}
                              onChange={(e) => {
                                field.onChange(e);
                                handleCountryChange(e.target.value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                    <p className="text-[0.8rem] text-muted-foreground">Country is used to auto-detect timezone &amp; currency on blur.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="timezone" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-muted-foreground" /> Timezone</FormLabel>
                        <Select
                          value={field.value || undefined}
                          onValueChange={(val) => {
                            field.onChange(val);
                            setIsDirty(true);
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select timezone…" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {timezoneGroups.map((group) => (
                              <SelectGroup key={group.label}>
                                <SelectLabel>{group.label}</SelectLabel>
                                {group.zones.map((tz) => (
                                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>Used to send reminders and generate recurring documents at the right local time.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="currencyLabel" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <FormControl><Input placeholder="USD" maxLength={6} {...field} /></FormControl>
                        <FormDescription>ISO 4217 code, e.g. GBP, AUD</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <Separator />
                  <FormField control={form.control} name="replyToEmail" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> Reply-to Email</FormLabel>
                      <FormControl><Input type="email" placeholder="replies@yourcompany.com" {...field} /></FormControl>
                      <FormDescription>When customers reply to invoice or quote emails, their replies will go to this address instead of the default no-reply sender.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                </CardContent>
              </Card>
            </CollapsibleSection>

            {/* ── TAXES ─────────────────────────────── */}
            <CollapsibleSection
              id="section-taxes"
              title="Taxes"
              icon={<ReceiptText className="w-5 h-5" />}
              isOpen={openSections["section-taxes"]}
              onToggle={() => toggleSection("section-taxes")}
            >
              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardDescription>Tax lines are applied to both quotes and invoices. Configure them once here.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-5">
                  {qboManagesTax && (
                    <div className="flex flex-col gap-3 rounded-md border border-sky-200 bg-sky-50/60 dark:border-sky-900 dark:bg-sky-950/30 p-3">
                      <div className="flex items-start gap-2">
                        <Lock className="w-4 h-4 text-sky-700 dark:text-sky-300 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-sky-900 dark:text-sky-100">
                            Managed by QuickBooks{qboCompanyName ? ` — ${qboCompanyName}` : ""}
                          </p>
                          <p className="text-xs text-sky-800/80 dark:text-sky-200/80 mt-0.5">
                            {qboTaxDirection === "use_qbo"
                              ? "Tax labels mirror your QuickBooks tax codes. Use Refresh below to pull the latest, or manage them in QuickBooks. Disconnect QuickBooks under Integrations to take back control."
                              : "Tax labels were pushed to QuickBooks at setup. To add or change them, manage them in QuickBooks, then disconnect to edit them locally."}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pl-6">
                        {qboTaxDirection === "use_qbo" && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={refreshQboTaxLabels.isPending}
                            onClick={() => {
                              refreshQboTaxLabels.mutate(undefined, {
                                onSuccess: (res) => {
                                  toast({
                                    title: "Tax labels refreshed",
                                    description: `${res.added} active tax code${res.added === 1 ? "" : "s"} pulled from QuickBooks.`,
                                  });
                                  void queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
                                },
                                onError: (err) => {
                                  toast({
                                    title: "Refresh failed",
                                    description: err instanceof Error ? err.message : "Could not refresh tax labels from QuickBooks.",
                                    variant: "destructive",
                                  });
                                },
                              });
                            }}
                          >
                            {refreshQboTaxLabels.isPending ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Refreshing…</>
                            ) : (
                              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh from QuickBooks</>
                            )}
                          </Button>
                        )}
                        <a
                          href={qboRealmId ? `https://app.qbo.intuit.com/app/salestaxcenter?cid=${qboRealmId}` : "https://app.qbo.intuit.com/app/salestaxcenter"}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 dark:text-sky-300 hover:underline"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Manage in QuickBooks
                        </a>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium mb-3 block">Tax Lines</Label>
                    {taxLines.length === 0 && !qboManagesTax && (
                      <p className="text-sm text-muted-foreground mb-3">No tax lines added. Click below to add one.</p>
                    )}
                    {taxLines.length === 0 && qboManagesTax && qboTaxDirection === "use_qbo" && (
                      <p className="text-sm text-muted-foreground mb-3">
                        No tax codes pulled yet. Click <span className="font-medium">Refresh from QuickBooks</span> above to pull active tax codes.
                      </p>
                    )}
                    <div className="space-y-2">
                      {taxLines.map((t, i) => {
                        // Task #762: QuickBooks integration is hidden behind
                        // "Coming Soon" — ignore stale QB ownership tags so
                        // existing rows behave like normal local labels and
                        // don't show "Managed by QuickBooks" badges.
                        const lineManaged = !!t.externalSource && t.externalSource !== "quickbooks";
                        const sourceLabel =
                          t.externalSource === "xero" ? "Xero" :
                          t.externalSource && t.externalSource !== "quickbooks" ? t.externalSource :
                          "";
                        // While the integration manages this section globally, lock every row.
                        // If only this row is integration-tagged but the integration is in skip
                        // mode (or disconnected), we still lock just name+rate per the snapshot tag.
                        const lockNameRate = qboManagesTax || lineManaged;
                        const lockRow = qboManagesTax;
                        const tagSource = lineManaged ? sourceLabel : (qboManagesTax ? "QuickBooks" : "");
                        return (
                          <div key={i} className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="Tax name (e.g. GST)"
                                value={t.name}
                                onChange={e => updateTaxLine(i, "name", e.target.value)}
                                className="flex-1 h-8 text-sm"
                                disabled={lockNameRate}
                                title={lockNameRate ? `Managed by ${tagSource || "your accounting integration"}` : undefined}
                              />
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  placeholder="5"
                                  value={t.rate ?? ""}
                                  onChange={e => { const parsed = parseFloat(e.target.value); updateTaxLine(i, "rate", e.target.value === "" || !Number.isFinite(parsed) ? undefined : parsed); }}
                                  className="w-20 h-8 text-sm"
                                  disabled={lockNameRate && qboTaxDirection !== "use_qbo"}
                                  title={lockNameRate && qboTaxDirection !== "use_qbo" ? `Managed by ${tagSource || "your accounting integration"}` : undefined}
                                />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                              <Input
                                placeholder="Reg# (optional)"
                                value={t.taxId ?? ""}
                                onChange={e => updateTaxLine(i, "taxId", e.target.value)}
                                className="w-36 h-8 text-sm"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                                onClick={() => removeTaxLine(i)}
                                disabled={lockRow}
                                title={lockRow ? `Disconnect ${tagSource || "the accounting integration"} to remove labels` : undefined}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {lineManaged && !qboManagesTax && (
                              <p className="text-[11px] text-muted-foreground pl-1">
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900">
                                  Managed by {sourceLabel}
                                </span>
                                <span className="ml-2">Name &amp; rate are kept in sync with {sourceLabel}. Edit the Reg # only.</span>
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {!qboManagesTax && (
                      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={addTaxLine}>
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Tax Line
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </CollapsibleSection>

            {/* ── QUOTE TEMPLATE ─────────────────────────── */}
            <CollapsibleSection
              id="section-quote-template"
              title="Quote Template"
              icon={<FileText className="w-5 h-5" />}
              isOpen={openSections["section-quote-template"]}
              onToggle={() => toggleSection("section-quote-template")}
            >

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-muted-foreground" /> Margin target
                  </CardTitle>
                  <CardDescription>The margin you aim for on every job. Only you see it; it's never on a quote or invoice.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <FormField control={form.control} name="targetMargin" render={({ field }) => (
                    <FormItem className="flex items-start justify-between gap-4">
                      <div>
                        <FormLabel className="text-sm font-medium">Target margin</FormLabel>
                        <FormDescription className="text-xs max-w-md">
                          Margins at or above this show green, below it amber, and under half of it red, on quotes, line items and projects.
                          The Internal Workshop shows how much to raise the price when a quote falls short.
                        </FormDescription>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <div className="relative w-28 shrink-0">
                          <Input type="number" inputMode="numeric" min={0} max={90} step={1} className="pr-8 text-right tabular-nums" {...field} value={field.value ?? ""} />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">%</span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" /> Document
                  </CardTitle>
                  <CardDescription>Controls how your customer quote PDF looks.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="documentTitle" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Document Title</FormLabel>
                        <FormControl><Input placeholder="QUOTATION" {...field} /></FormControl>
                        <FormDescription>e.g. QUOTATION, QUOTE, PROPOSAL</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="pt-1 flex flex-col gap-2">
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={handleQuotePdfPreview}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Preview customer PDF
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1.5">See how your quote PDF will look using current settings. Uses sample data — no document is saved.</p>
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => setShowEmailPreview(true)}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Preview customer email
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1.5">See how your branding looks in a customer-facing email.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Columns className="w-4 h-4 text-muted-foreground" /> Line Item Columns
                  </CardTitle>
                  <CardDescription>Choose which columns appear in the customer PDF and what to call them.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <div className="space-y-4">
                    {colKeys.map(({ key, defaultLabel }) => {
                      const isLastVisible = columnConfig[key].visible && visibleColCount <= 1;
                      return (
                        <div key={key} className="flex items-center gap-4">
                          <Switch
                            checked={columnConfig[key].visible}
                            disabled={isLastVisible}
                            title={isLastVisible ? "At least one column must be visible" : undefined}
                            onCheckedChange={val => updateColConfig(key, "visible", val)}
                          />
                          <div className="flex-1 min-w-0">
                            <Label className="text-xs text-muted-foreground mb-1 block">{defaultLabel}</Label>
                            <Input
                              placeholder={defaultLabel}
                              value={columnConfig[key].label}
                              onChange={e => updateColConfig(key, "label", e.target.value)}
                              disabled={!columnConfig[key].visible}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ReceiptText className="w-4 h-4 text-muted-foreground" /> Notes / Terms
                  </CardTitle>
                  <CardDescription>Printed at the bottom of every customer PDF. Great for payment terms, warranties, or disclaimers.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <FormField control={form.control} name="termsAndNotes" render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder={"Pricing reflects the full scope of work; changes may affect pricing.\nPayment is due within 30 days of completion.\nDeposits are non-refundable once materials have been ordered."}
                          className="min-h-[120px] resize-none text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" /> PDF Footer
                  </CardTitle>
                  <CardDescription>Appears at the bottom of every page of the customer quote PDF, alongside the page number.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <ProGate feature="INVOICE_FOOTER" mode="overlay">
                  <FormField control={form.control} name="pdfFooter" render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. www.yourcompany.com • Licensed & Insured"
                          className="min-h-[72px] resize-none text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>Leave blank to show only the page number.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                  </ProGate>
                </CardContent>
              </Card>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
                <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Template tip:</span> All quote template settings are applied when you export a Customer PDF from any quote. The cost report PDF uses a fixed layout regardless of these settings.
                </div>
              </div>
            </CollapsibleSection>

            {/* ── INVOICE TEMPLATE ─────────────────────────── */}
            <CollapsibleSection
              id="section-invoice-template"
              title="Invoice Template"
              icon={<FileText className="w-5 h-5" />}
              isOpen={openSections["section-invoice-template"]}
              onToggle={() => toggleSection("section-invoice-template")}
            >

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" /> Document
                  </CardTitle>
                  <CardDescription>Controls how your customer invoice PDF looks.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="invoiceDocumentTitle" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Document Title</FormLabel>
                        <FormControl><Input placeholder="INVOICE" {...field} /></FormControl>
                        <FormDescription>e.g. INVOICE, TAX INVOICE</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="invoiceCurrencyLabel" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <FormControl><Input placeholder={form.watch("currencyLabel") || "USD"} maxLength={6} {...field} /></FormControl>
                        <FormDescription>Leave blank to use quote setting</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={handleInvoicePdfPreview}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview customer PDF
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1.5">See how your invoice PDF will look using current settings. Uses sample data — no document is saved.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Columns className="w-4 h-4 text-muted-foreground" /> Line Item Columns
                  </CardTitle>
                  <CardDescription>Choose which columns appear in the invoice PDF and what to call them.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <div className="space-y-4">
                    {colKeys.map(({ key, defaultLabel }) => {
                      const isLastVisible = invoiceColumnConfig[key].visible && invoiceVisibleColCount <= 1;
                      return (
                        <div key={key} className="flex items-center gap-4">
                          <Switch
                            checked={invoiceColumnConfig[key].visible}
                            disabled={isLastVisible}
                            title={isLastVisible ? "At least one column must be visible" : undefined}
                            onCheckedChange={val => updateInvoiceColConfig(key, "visible", val)}
                          />
                          <div className="flex-1 min-w-0">
                            <Label className="text-xs text-muted-foreground mb-1 block">{defaultLabel}</Label>
                            <Input
                              placeholder={defaultLabel}
                              value={invoiceColumnConfig[key].label}
                              onChange={e => updateInvoiceColConfig(key, "label", e.target.value)}
                              disabled={!invoiceColumnConfig[key].visible}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ReceiptText className="w-4 h-4 text-muted-foreground" /> Notes / Terms
                  </CardTitle>
                  <CardDescription>Printed at the bottom of every invoice customer PDF.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <FormField control={form.control} name="invoiceTermsAndNotes" render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder={"Payment is due within 30 days of the invoice date.\nLate payments may be subject to a 1.5% monthly fee."}
                          className="min-h-[120px] resize-none text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" /> PDF Footer
                  </CardTitle>
                  <CardDescription>Appears at the bottom of every page of the invoice customer PDF, alongside the page number.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5">
                  <ProGate feature="INVOICE_FOOTER" mode="overlay">
                  <FormField control={form.control} name="invoicePdfFooter" render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. www.yourcompany.com • Licensed & Insured"
                          className="min-h-[72px] resize-none text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>Leave blank to show only the page number.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                  </ProGate>
                </CardContent>
              </Card>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
                <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Template tip:</span> Invoice template settings apply to the invoice customer PDF. Company info (logo, name, address) is shared across both quotes and invoices.
                </div>
              </div>
            </CollapsibleSection>

            {/* ── EMAIL DEFAULTS ─────────────────────────────── */}
            <CollapsibleSection
              id="section-email-defaults"
              title="Email Defaults"
              icon={<Mail className="w-5 h-5" />}
              isOpen={openSections["section-email-defaults"]}
              onToggle={() => toggleSection("section-email-defaults")}
            >
              <ProGate feature="EMAIL_DEFAULTS" mode="overlay">
              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" /> Default Email Content
                  </CardTitle>
                  <CardDescription>
                    Pre-fill the subject and body when sending quote or invoice emails. Supported variables:{" "}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{"{customerName}"}</code>{" "}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{"{quoteNumber}"}</code>{" "}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{"{invoiceNumber}"}</code>{" "}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">{"{companyName}"}</code>
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Quote Emails</h4>
                    <FormField control={form.control} name="defaultQuoteEmailSubject" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="Quote {quoteNumber} from {companyName}" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="defaultQuoteEmailBody" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Body</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={"Hi {customerName},\n\nPlease find your quote attached. Let us know if you have any questions.\n\nThank you for your business!"}
                            className="min-h-[120px] resize-y text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <Separator />
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-foreground">Invoice Emails</h4>
                    <FormField control={form.control} name="defaultInvoiceEmailSubject" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="Invoice {invoiceNumber} from {companyName}" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="defaultInvoiceEmailBody" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Body</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={"Hi {customerName},\n\nPlease find your invoice attached. Payment is due as per the invoice terms.\n\nThank you for your business!"}
                            className="min-h-[120px] resize-y text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </CardContent>
              </Card>
              </ProGate>
            </CollapsibleSection>

            {/* ── SERVICE AGREEMENT ─────────────────────────── */}
            <CollapsibleSection
              id="section-service-agreement"
              title="Additional Email Attachments"
              icon={<FileSignature className="w-5 h-5" />}
              isOpen={openSections["section-service-agreement"]}
              onToggle={() => toggleSection("section-service-agreement")}
            >
              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-muted-foreground" /> Service Agreement / Terms PDF
                  </CardTitle>
                  <CardDescription>
                    Upload a PDF (max 10 MB) and we'll automatically attach it to outgoing quote emails alongside the quote PDF.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  <input
                    ref={serviceAgreementInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleServiceAgreementUpload}
                  />
                  {settings?.serviceAgreementFileName ? (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/60 bg-muted/30">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{settings.serviceAgreementFileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {settings.serviceAgreementContentSize
                              ? `${(settings.serviceAgreementContentSize / 1024).toFixed(0)} KB`
                              : ""}
                            {settings.serviceAgreementUploadedAt
                              ? ` • Uploaded ${new Date(settings.serviceAgreementUploadedAt).toLocaleDateString()}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={handleDownloadServiceAgreement}
                        >
                          <Download className="w-3.5 h-3.5" /> View
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => { setServiceAgreementError(null); serviceAgreementInputRef.current?.click(); }}
                          disabled={serviceAgreementUploading}
                        >
                          {serviceAgreementUploading
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Upload className="w-3.5 h-3.5" />}
                          Replace
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-destructive hover:text-destructive"
                          onClick={handleRemoveServiceAgreement}
                          disabled={deleteServiceAgreementMutation.isPending}
                        >
                          {deleteServiceAgreementMutation.isPending
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Trash2 className="w-3.5 h-3.5" />}
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => { setServiceAgreementError(null); serviceAgreementInputRef.current?.click(); }}
                      disabled={serviceAgreementUploading}
                    >
                      {serviceAgreementUploading
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Upload className="w-4 h-4" />}
                      Upload PDF
                    </Button>
                  )}
                  {serviceAgreementError && (
                    <p className="text-sm text-destructive">{serviceAgreementError}</p>
                  )}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                    <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      When sending a quote, you'll see a checkbox to attach this agreement (on by default). Customers receive a single email with both PDFs.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </CollapsibleSection>

            {/* ── NOTIFICATIONS ─────────────────────────────── */}
            <CollapsibleSection
              id="section-notifications"
              title="Notifications"
              icon={<Bell className="w-5 h-5" />}
              isOpen={openSections["section-notifications"]}
              onToggle={() => toggleSection("section-notifications")}
            >

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="w-4 h-4 text-muted-foreground" /> Email Notifications
                  </CardTitle>
                  <CardDescription>
                    Automatic emails sent to your company email address. Make sure your company email is filled in above.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-6">
                  <FormField control={form.control} name="notifyOverdueInvoices" render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-4">
                      <div>
                        <FormLabel className="text-sm font-medium">Overdue invoice reminders</FormLabel>
                        <FormDescription className="text-xs">Get an email listing any invoices that are past their due date.</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value ?? false} onCheckedChange={(v) => { field.onChange(v); setIsDirty(true); }} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <div className="space-y-4">
                    <FormField control={form.control} name="notifyTodoReminders" render={({ field }) => (
                      <FormItem className="flex items-center justify-between gap-4">
                        <div>
                          <FormLabel className="text-sm font-medium">Upcoming to-do reminders</FormLabel>
                          <FormDescription className="text-xs">Get an email when a task deadline is approaching.</FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value ?? false} onCheckedChange={(v) => { field.onChange(v); setIsDirty(true); }} />
                        </FormControl>
                      </FormItem>
                    )} />
                    {form.watch("notifyTodoReminders") && (
                      <FormField control={form.control} name="todoReminderLeadDays" render={({ field }) => (
                        <FormItem className="flex items-center gap-4 pl-0">
                          <FormLabel className="text-sm whitespace-nowrap shrink-0">Remind me</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              max={30}
                              className="w-20 h-8 text-sm"
                              {...field}
                              onChange={e => { field.onChange(parseInt(e.target.value, 10) || 1); setIsDirty(true); }}
                            />
                          </FormControl>
                          <span className="text-sm text-muted-foreground whitespace-nowrap">day(s) before deadline</span>
                          <FormMessage />
                        </FormItem>
                      )} />
                    )}
                  </div>

                  <FormField control={form.control} name="notifyRecurringGenerated" render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-4">
                      <div>
                        <FormLabel className="text-sm font-medium">Recurring document generated</FormLabel>
                        <FormDescription className="text-xs">Get an email whenever QuoTrak auto-creates a recurring quote or invoice.</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value ?? false} onCheckedChange={(v) => { field.onChange(v); setIsDirty(true); }} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="notifyPaymentReceived" render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-4">
                      <div>
                        <FormLabel className="text-sm font-medium">Payment received</FormLabel>
                        <FormDescription className="text-xs">Get an email whenever a customer pays an invoice online.</FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value ?? false} onCheckedChange={(v) => { field.onChange(v); setIsDirty(true); }} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="notificationHour" render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-4">
                      <div>
                        <FormLabel className="text-sm font-medium">Send daily reminders at</FormLabel>
                        <FormDescription className="text-xs">The local hour (based on your timezone) when reminders and recurring jobs run.</FormDescription>
                      </div>
                      <FormControl>
                        <Select
                          value={String(field.value ?? 6)}
                          onValueChange={(v) => { field.onChange(Number(v)); setIsDirty(true); }}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent position="popper">
                            {Array.from({ length: 24 }, (_, i) => {
                              const label = i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i - 12}:00 PM`;
                              return <SelectItem key={i} value={String(i)}>{label}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border/50">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" /> Marketing Emails
                  </CardTitle>
                  <CardDescription>
                    Occasional emails from QuoTrak to help you get the most out of the app.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">QuoTrak news and tips</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Occasional emails about new features and offers, and help getting started. Important notices about your account or our policies still reach you.</p>
                    </div>
                    <Switch
                      checked={reEngagementEnabled}
                      onCheckedChange={(v) => { setReEngagementEnabled(v); setIsDirty(true); }}
                    />
                  </div>
                </CardContent>
              </Card>
            </CollapsibleSection>

          </form>
        </Form>

        <Separator className="my-2" />

        {/* ── BILLING ─────────────────────────────── */}
        <CollapsibleSection
          id="section-billing"
          title="Billing"
          icon={<CreditCard className="w-5 h-5" />}
          isOpen={openSections["section-billing"]}
          onToggle={() => toggleSection("section-billing")}
          spacing="space-y-4"
        >
          {isPro ? (
            <Card className="shadow-sm">
              <CardHeader className="pb-3 border-b bg-muted/40">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <CardTitle className="text-base">Pro Plan</CardTitle>
                </div>
                <CardDescription>You're on the Pro plan. Manage your subscription, payment method, or cancel below.</CardDescription>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                    <Sparkles className="w-3 h-3" /> Pro
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {subscriptionInfo?.cancelAtPeriodEnd
                      ? "Cancels"
                      : "Renews"}{" "}
                    {subscriptionInfo?.currentPeriodEnd
                      ? new Date(subscriptionInfo.currentPeriodEnd * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
                      : "Active subscription"}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBillingPortal}
                  disabled={billingPortalLoading}
                >
                  {billingPortalLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening portal…</>
                  ) : (
                    <><ExternalLink className="w-4 h-4 mr-2" /> Manage Billing</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Update your payment method, view invoices, or cancel your subscription in the Stripe customer portal.
                </p>
              </CardContent>
            </Card>
          ) : (
            <ProGate feature="BILLING" mode="overlay">
              <Card className="shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/40">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-muted-foreground" />
                    <CardTitle className="text-base">Pro Plan</CardTitle>
                  </div>
                  <CardDescription>Upgrade to Pro to manage your subscription, payment method, and more.</CardDescription>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-full">
                      <Sparkles className="w-3 h-3" /> Free
                    </span>
                  </div>
                  <Button type="button" variant="outline" disabled className="opacity-50">
                    <ExternalLink className="w-4 h-4 mr-2" /> Manage Billing
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Unlock email sending, recurring invoices, custom branding, and more with the Pro plan.
                  </p>
                </CardContent>
              </Card>
            </ProGate>
          )}
        </CollapsibleSection>

        <Separator className="my-2" />

        {/* ── ACCEPT PAYMENTS ─────────────────────────────── */}
        <CollapsibleSection
          id="section-accept-payments"
          title="Accept Payments"
          icon={<Zap className="w-5 h-5" />}
          isOpen={openSections["section-accept-payments"]}
          onToggle={() => toggleSection("section-accept-payments")}
          spacing="space-y-4"
        >
          <Card className="shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/40">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-base">Stripe Connect</CardTitle>
              </div>
              <CardDescription>
                Connect your Stripe account to accept online payments directly on your invoices. Your customers pay through a hosted Stripe Checkout page and the money goes straight to your account.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {!isPro ? (
                <ProGate feature="STRIPE_CONNECT" mode="overlay">
                  <div className="space-y-4">
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Connect your Stripe account to:</p>
                      <ul className="list-disc list-inside space-y-0.5 pl-1">
                        <li>Generate shareable payment links on sent invoices</li>
                        <li>Let customers pay online with a card</li>
                        <li>Automatically mark invoices as paid when payment completes</li>
                      </ul>
                    </div>
                    <Button type="button" disabled className="opacity-50">
                      <Zap className="w-4 h-4 mr-2" /> Connect Stripe
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      You'll be taken to Stripe to create or connect your Express account. This is free — no platform fee is charged on payments.
                    </p>
                  </div>
                </ProGate>
              ) : connectStatus?.connected && connectStatus.status === "active" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Stripe account connected</p>
                      <p className="text-xs text-green-700">You can now generate payment links on sent invoices.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleConnectDashboard}
                      disabled={connectDashboardLoading}
                    >
                      {connectDashboardLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</>
                      ) : (
                        <><ExternalLink className="w-4 h-4 mr-2" /> Go to Stripe Dashboard</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={handleConnectOnboarding}
                      disabled={connectOnboardingLoading}
                    >
                      {connectOnboardingLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</>
                      ) : (
                        "Update account details"
                      )}
                    </Button>
                  </div>
                </div>
              ) : connectStatus?.connected && connectStatus.status === "disabled" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Account setup incomplete</p>
                      <p className="text-xs text-yellow-700">Your Stripe account setup hasn't been completed. Resume onboarding to finish setting up payouts.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handleConnectOnboarding}
                      disabled={connectOnboardingLoading}
                    >
                      {connectOnboardingLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                      ) : (
                        <>Resume Onboarding</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => refetchConnectStatus()}
                    >
                      Refresh status
                    </Button>
                  </div>
                </div>
              ) : connectStatus?.connected && connectStatus.status === "pending" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Onboarding in progress</p>
                      <p className="text-xs text-yellow-700">Complete your Stripe onboarding to start accepting payments.</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleConnectOnboarding}
                    disabled={connectOnboardingLoading}
                  >
                    {connectOnboardingLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</>
                    ) : (
                      <><ExternalLink className="w-4 h-4 mr-2" /> Continue Stripe Onboarding</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => refetchConnectStatus()}
                  >
                    Check status
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Connect your Stripe account to:</p>
                    <ul className="list-disc list-inside space-y-0.5 pl-1">
                      <li>Generate shareable payment links on sent invoices</li>
                      <li>Let customers pay online with a card</li>
                      <li>Automatically mark invoices as paid when payment completes</li>
                    </ul>
                  </div>
                  <Button
                    type="button"
                    onClick={handleConnectOnboarding}
                    disabled={connectOnboardingLoading}
                  >
                    {connectOnboardingLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" /> Connect Stripe</>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    You'll be taken to Stripe to create or connect your Express account. This is free — no platform fee is charged on payments.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </CollapsibleSection>

        <Separator className="my-2" />

        {/* ── ACCOUNT ─────────────────────────────── */}
        <CollapsibleSection
          id="section-account"
          title="Account"
          icon={<User className="w-5 h-5" />}
          isOpen={openSections["section-account"]}
          onToggle={() => toggleSection("section-account")}
          spacing="space-y-4"
        >
          {/* Update Name + Labor Rate */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/40">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-base">Profile</CardTitle>
              </div>
              <CardDescription>Update your display name and default labor rate.</CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="flex gap-2">
                <Input
                  value={profileName}
                  onChange={(e) => { setProfileName(e.target.value); setProfileStatus(null); }}
                  placeholder="Your name"
                  disabled={profileLoading}
                  className="max-w-xs"
                />
              </div>
              <div>
                <Button
                  type="button"
                  onClick={handleUpdateProfile}
                  disabled={profileLoading || !profileName.trim()}
                >
                  {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  {profileLoading ? "Saving…" : "Save"}
                </Button>
              </div>
              {profileStatus?.ok && (
                <div className="flex items-center gap-1.5 text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  Profile updated successfully.
                </div>
              )}
              {profileStatus?.error && (
                <div className="flex items-center gap-1.5 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  {profileStatus.error}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Email */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/40">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-base">Change Email</CardTitle>
              </div>
              <CardDescription>
                Enter a new email address. A verification link will be sent to it.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-3">
              <p className="text-sm text-muted-foreground">
                Current email: <span className="font-medium text-foreground">{user?.email}</span>
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setEmailStatus(null); }}
                  placeholder="new@example.com"
                  disabled={emailLoading}
                  className="max-w-xs"
                />
                <Button
                  type="button"
                  onClick={handleChangeEmail}
                  disabled={emailLoading || !newEmail.trim() || !newEmail.includes("@")}
                >
                  {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
                  {emailLoading ? "Sending…" : "Send Verification"}
                </Button>
              </div>
              {emailStatus?.ok && (
                <div className="flex items-center gap-1.5 text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  Verification email sent. Please check your inbox to confirm the change.
                </div>
              )}
              {emailStatus?.error && (
                <div className="flex items-center gap-1.5 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  {emailStatus.error}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password — hidden for OAuth users */}
          {user?.hasPassword && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3 border-b bg-muted/40">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-muted-foreground" />
                  <CardTitle className="text-base">Change Password</CardTitle>
                </div>
                <CardDescription>Update your login password.</CardDescription>
              </CardHeader>
              <CardContent className="pt-5 space-y-3">
                <div className="space-y-2 max-w-xs">
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => { setCurrentPassword(e.target.value); setPasswordStatus(null); }}
                    placeholder="Current password"
                    disabled={passwordLoading}
                    autoComplete="current-password"
                  />
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordStatus(null); }}
                    placeholder="New password (min. 8 characters)"
                    disabled={passwordLoading}
                    autoComplete="new-password"
                  />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordStatus(null); }}
                    placeholder="Confirm new password"
                    disabled={passwordLoading}
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleChangePassword}
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                >
                  {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
                  {passwordLoading ? "Updating…" : "Update Password"}
                </Button>
                {passwordStatus?.ok && (
                  <div className="flex items-center gap-1.5 text-sm text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    Password updated successfully.
                  </div>
                )}
                {passwordStatus?.error && (
                  <div className="flex items-center gap-1.5 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    {passwordStatus.error}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Log Out */}
          <Card className="shadow-sm border-destructive/20">
            <CardHeader className="pb-3 border-b bg-muted/40">
              <CardTitle className="text-base">Session</CardTitle>
              <CardDescription>Manage your account session.</CardDescription>
            </CardHeader>
            <CardContent className="pt-5">
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={async () => { await logout(); navigate("/login"); }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </CardContent>
          </Card>
        </CollapsibleSection>

        <Separator className="my-2" />

        {/* ── DANGER ZONE ─────────────────────────────── */}
        <CollapsibleSection
          id="section-danger"
          title="Danger Zone"
          icon={<ShieldAlert className="w-5 h-5" />}
          isOpen={openSections["section-danger"]}
          onToggle={() => toggleSection("section-danger")}
          isDanger
          spacing="space-y-4"
        >

          <Card className="shadow-sm border-destructive/50 bg-destructive/[0.02]">
            <CardHeader className="pb-3 border-b border-destructive/20 bg-muted/40">
              <CardTitle className="text-base text-destructive">Delete Account</CardTitle>
              <CardDescription>
                Permanently delete your account and all associated data. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="text-sm text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">The following will be permanently deleted:</p>
                <ul className="list-disc list-inside space-y-0.5 pl-1 text-muted-foreground">
                  <li>All estimates and quotes</li>
                  <li>All invoices</li>
                  <li>All customers</li>
                  <li>All products and cost catalog entries</li>
                  <li>All to-dos and tasks</li>
                  <li>All file attachments</li>
                  <li>Your company settings and preferences</li>
                  <li>Your user account</li>
                </ul>
              </div>
              <Button
                variant="destructive"
                onClick={() => { setDeleteDialogOpen(true); setDeleteEmailInput(""); setDeleteError(null); }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </CollapsibleSection>

        {/* Quote PDF preview dialog */}
        <Dialog
          open={showQuotePdfPreview}
          onOpenChange={(open) => {
            if (!open && quotePdfBlobUrl) {
              URL.revokeObjectURL(quotePdfBlobUrl);
              setQuotePdfBlobUrl(null);
            }
            setShowQuotePdfPreview(open);
          }}
        >
          <DialogContent className="max-w-3xl w-full p-0 overflow-hidden">
            <DialogHeader className="px-6 pt-5 pb-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" /> Customer quote PDF preview
              </DialogTitle>
              <DialogDescription>
                This uses sample data to show how your quote PDF will look. No document is saved.
              </DialogDescription>
            </DialogHeader>
            <div className="p-0 bg-muted/40" style={{ height: 560 }}>
              {quotePdfBlobUrl && (
                <iframe
                  src={quotePdfBlobUrl}
                  title="Quote PDF preview"
                  className="w-full h-full rounded border-0"
                  style={{ height: 560 }}
                />
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t">
              <Button variant="outline" onClick={() => setShowQuotePdfPreview(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice PDF preview dialog */}
        <Dialog
          open={showInvoicePdfPreview}
          onOpenChange={(open) => {
            if (!open && invoicePdfBlobUrl) {
              URL.revokeObjectURL(invoicePdfBlobUrl);
              setInvoicePdfBlobUrl(null);
            }
            setShowInvoicePdfPreview(open);
          }}
        >
          <DialogContent className="max-w-3xl w-full p-0 overflow-hidden">
            <DialogHeader className="px-6 pt-5 pb-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" /> Customer invoice PDF preview
              </DialogTitle>
              <DialogDescription>
                This uses sample data to show how your invoice PDF will look. No document is saved.
              </DialogDescription>
            </DialogHeader>
            <div className="p-0 bg-muted/40" style={{ height: 560 }}>
              {invoicePdfBlobUrl && (
                <iframe
                  src={invoicePdfBlobUrl}
                  title="Invoice PDF preview"
                  className="w-full h-full rounded border-0"
                  style={{ height: 560 }}
                />
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t">
              <Button variant="outline" onClick={() => setShowInvoicePdfPreview(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email preview dialog */}
        <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
          <DialogContent className="max-w-2xl w-full p-0 overflow-hidden">
            <DialogHeader className="px-6 pt-5 pb-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" /> Customer email preview
              </DialogTitle>
              <DialogDescription>
                This is how your branding appears in emails sent to customers. No email is sent.
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-muted/40 overflow-auto max-h-[70vh]">
              {(() => {
                const accent = form.watch("accentColor") || "#1e3a5f";
                const rawCompany = form.watch("companyName") || "Your Company";
                const company = rawCompany.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
                const logo = logoPreview ?? settings?.logoBase64 ?? null;
                const logoRow = logo
                  ? `<tr><td style="background:#ffffff;padding:24px 32px;text-align:center;"><img src="${logo}" alt="${company}" style="max-height:56px;max-width:200px;display:inline-block;" /></td></tr>`
                  : "";
                const sampleBody = `
                  <h2 style="margin:0 0 16px;font-size:22px;color:#1a1a2e;">Quote #QT-0042 from ${company}</h2>
                  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:16px 20px;margin-bottom:24px;">
                    <table width="100%" cellpadding="0" cellspacing="0">
                      <tr><td style="padding:6px 0;font-size:14px;color:#64748b;width:140px;">Quote Number</td><td style="padding:6px 0;font-size:14px;color:#1a1a2e;font-weight:600;">QT-0042</td></tr>
                      <tr><td style="padding:6px 0;font-size:14px;color:#64748b;">Customer</td><td style="padding:6px 0;font-size:14px;color:#1a1a2e;">Alex Johnson</td></tr>
                      <tr><td style="padding:6px 0;font-size:14px;color:#64748b;">Total</td><td style="padding:6px 0;font-size:14px;color:#1a1a2e;font-weight:600;">$4,250.00</td></tr>
                      <tr><td style="padding:6px 0;font-size:14px;color:#64748b;">Valid Until</td><td style="padding:6px 0;font-size:14px;color:#1a1a2e;">May 15, 2026</td></tr>
                    </table>
                  </div>
                  <div style="font-size:15px;line-height:1.7;color:#374151;">Please find your quote attached. Feel free to reach out if you have any questions.</div>
                  <p style="margin:24px 0;text-align:center;"><a href="#" style="display:inline-block;background:${accent};color:#ffffff;font-size:15px;font-weight:600;padding:14px 32px;border-radius:6px;text-decoration:none;">View Quote</a></p>
                `;
                const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1.0"/></head><body style="margin:0;padding:0;background:#f4f6f8;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#1a1a2e;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f6f8;padding:32px 0;"><tr><td align="center"><table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,0.08);">${logoRow}<tr><td style="background:${accent};padding:24px 32px;text-align:center;"><p style="margin:0;font-size:20px;font-weight:700;color:#ffffff;">${company}</p></td></tr><tr><td style="padding:32px;">${sampleBody}</td></tr><tr><td style="background:#f8fafc;padding:16px 32px;border-top:1px solid #e2e8f0;"><p style="margin:0;font-size:12px;color:#94a3b8;text-align:center;">Created with QuoTrak — Professional quoting &amp; invoicing made simple. quotrak.com</p></td></tr></table></td></tr></table></body></html>`;
                return (
                  <iframe
                    srcDoc={html}
                    title="Email preview"
                    className="w-full rounded border-0"
                    style={{ height: 520 }}
                    sandbox="allow-same-origin"
                  />
                );
              })()}
            </div>
            <DialogFooter className="px-6 py-4 border-t">
              <Button variant="outline" onClick={() => setShowEmailPreview(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!isDeleting) { setDeleteDialogOpen(open); setDeleteEmailInput(""); setDeleteError(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" /> Permanently Delete Account
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <p className="font-semibold text-foreground">This action is irreversible.</p>
                  <p>All of your data will be permanently deleted, including:</p>
                  <ul className="list-disc list-inside space-y-0.5 pl-1 text-muted-foreground">
                    <li>Estimates, invoices, and customers</li>
                    <li>Products and cost catalog entries</li>
                    <li>To-dos and file attachments</li>
                    <li>Company settings and preferences</li>
                  </ul>
                  <p className="text-foreground">To confirm, type your email address below:</p>
                  <p className="font-mono text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{user?.email}</p>
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <Input
                placeholder="Enter your email to confirm"
                value={deleteEmailInput}
                onChange={(e) => { setDeleteEmailInput(e.target.value); setDeleteError(null); }}
                disabled={isDeleting}
                autoComplete="off"
                className="font-mono text-sm"
              />
              {deleteError && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{deleteError}</span>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setDeleteDialogOpen(false); setDeleteEmailInput(""); setDeleteError(null); }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteEmailInput.trim().toLowerCase() !== (user?.email ?? "").toLowerCase() || isDeleting}
              >
                {isDeleting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                ) : (
                  "Permanently Delete My Account"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}
